import { CommonModule } from '@angular/common';
import { Component, input, computed } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { NotFoundComponent } from '../../../components/not-found/not-found.component';
import { getDayOfWeek, toDate } from '../../../utils/date-formatter';
import { EventDto } from '@millionshow/api-client';

@Component({
  selector: 'app-booth-options',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NotFoundComponent],
  template: `
    <div class="flex flex-col gap-5">
      <h2 class="text-lg font-semibold text-zinc-800">부스/부대시설 신청</h2>

      @if (boothOptionsArray().length > 0) {
      <div
        class="border border-zinc-100 bg-white rounded-lg p-6 flex flex-col gap-4">
        @for (option of boothOptionsArray().controls; track $index) {
        <div class="flex flex-col gap-2">
          <div [formGroup]="option" class="flex justify-between items-center">
            <div class="flex flex-col flex-1">
              <div class="flex flex-col gap-0.5">
                <h4 class="text-zinc-800 font-bold">
                  {{ option.value.name }}
                </h4>
                <!--
                <p class="text-zinc-400 text-sm font-medium">
                  {{ option.value.unitPrice | number }}원 @if
                  (option.value.unit) {
                  <span class="text-zinc-400 text-sm font-medium"
                    >/ {{ option.value.unit }}</span
                  >
                  }
                </p>
                -->
                @if (option.value.priceInquiry) {
                <p
                  class="text-zinc-500 text-sm font-medium whitespace-pre-line">
                  {{ option.value.priceInquiry }}
                </p>
                }
              </div>
            </div>

            <div class="flex gap-2">
              <button
                type="button"
                class="qty-btn"
                (click)="decreaseQuantity(option)">
                −
              </button>
              <input
                formControlName="quantity"
                type="number"
                min="0"
                class="qty-input text-zinc-600"
                readonly />
              <button
                type="button"
                class="qty-btn"
                (click)="increaseQuantity(option)">
                +
              </button>
            </div>
          </div>
          @if (option.value.description) {
          <div
            class="text-sm text-zinc-500 whitespace-pre-line font-medium p-4 bg-zinc-50 rounded-lg">
            ※ {{ option.value.description }}
          </div>
          }
        </div>
        @if ($index < boothOptionsArray().length - 1) {
        <div class="w-full h-px bg-zinc-100"></div>
        } }
      </div>

      <div
        class="border border-zinc-100 bg-white rounded-lg p-6 flex flex-col gap-4">
        <!--
        <div
          class="flex items-start justify-between py-3 border-b border-zinc-100">
          <div class="flex flex-col gap-1">
            <span class="text-zinc-800 font-medium">계약금(3일 이내)</span>
          </div>
          <div class="flex flex-col items-end gap-1">
            <span class="text-zinc-800 font-semibold text-lg"
              >{{ calculateDeposit() | number }}원</span
            >
            <span class="text-zinc-400 text-sm">{{ depositDeadline() }}</span>
          </div>
        </div>

        <div
          class="flex items-start justify-between py-3 border-b border-zinc-100">
          <div class="flex flex-col gap-1">
            <span class="text-zinc-800 font-medium"
              >잔금(행사 한달전 이내)</span
            >
          </div>
          <div class="flex flex-col items-end gap-1">
            <span class="text-zinc-800 font-semibold text-lg"
              >{{ calculateBalance() | number }}원</span
            >
            <span class="text-zinc-400 text-sm">{{ balanceDeadline() }}</span>
          </div>
        </div>

        <div
          class="flex items-center justify-end py-4 bg-zinc-50 rounded-lg px-4">
          <div class="flex items-baseline gap-2">
            <span class="text-zinc-600 text-sm">합계</span>
            <span class="text-primary-500 font-bold text-2xl"
              >{{ calculateTotal() | number }}원</span
            >
          </div>
        </div>
        -->

        <!-- 안내 문구 -->
        <p class="text-zinc-500 text-sm mt-2">
          * 상세 견적은 신청서 제출 후 해당 담당자를 통해 전달드립니다.
        </p>
      </div>
      } @else {
      <app-not-found [isLoading]="isLoading()">
        <p class="text-zinc-500">등록된 부스 옵션이 없습니다.</p>
      </app-not-found>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .qty-btn {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: none;
        background: #fafafa;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.25rem;
        color: #a1a1aa;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .qty-btn:hover {
        background: #e4e4e7;
      }

      .qty-btn:active {
        transform: scale(0.95);
      }

      .qty-input {
        width: 60px;
        height: 32px;
        text-align: center;
        border: none;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 600;
        background: #fafafa;
        color: #a1a1aa;
        pointer-events: none;
      }

      .qty-input::-webkit-outer-spin-button,
      .qty-input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      @media (min-width: 1024px) {
        .qty-btn {
          width: 48px;
          height: 48px;
          font-size: 1.5rem;
        }

        .qty-input {
          width: 80px;
          height: 48px;
          border-radius: 12px;
          font-size: 1.125rem;
        }
      }
    `,
  ],
})
export class BoothOptionsComponent {
  boothOptionsArray = input.required<FormArray<FormGroup>>();
  isLoading = input<boolean>(false);
  event = input<EventDto | undefined>();

  increaseQuantity(control: AbstractControl): void {
    const currentValue = control.value.quantity || 0;
    control.patchValue({ quantity: currentValue + 1 });
  }

  decreaseQuantity(control: AbstractControl): void {
    const currentValue = control.value.quantity || 0;
    if (currentValue > 0) {
      control.patchValue({ quantity: currentValue - 1 });
    }
  }

  calculateTotal(): number {
    return this.boothOptionsArray().controls.reduce((sum, control) => {
      const unitPrice = control.value.unitPrice || 0;
      const quantity = control.value.quantity || 0;
      return sum + unitPrice * quantity;
    }, 0);
  }

  calculateDeposit(): number {
    return this.calculateTotal() * 0.5;
  }

  calculateBalance(): number {
    return this.calculateTotal() - this.calculateDeposit();
  }

  depositDeadline = computed(() => {
    const applicationDate = new Date();
    const deadline = new Date(applicationDate);
    deadline.setDate(deadline.getDate() + 3);

    const year = String(deadline.getFullYear()).slice(2);
    const month = deadline.getMonth() + 1;
    const day = deadline.getDate();
    const weekday = getDayOfWeek(deadline);

    return `${year}년 ${month}월 ${day}일(${weekday})까지 납부`;
  });

  balanceDeadline = computed(() => {
    const eventData = this.event();

    if (!eventData?.startAt) {
      return '행사 날짜 확인 필요';
    }

    const eventDate = toDate(eventData.startAt);
    const deadline = new Date(eventDate);
    deadline.setMonth(deadline.getMonth() - 1);

    const year = String(deadline.getFullYear()).slice(2);
    const month = deadline.getMonth() + 1;
    const day = deadline.getDate();
    const weekday = getDayOfWeek(deadline);

    return `${year}년 ${month}월 ${day}일(${weekday})까지 납부`;
  });
}
